import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { can } from '../../../components/Authorize';
import { CONFIG } from '../../../components/Authorize/permissions/resources';
import { displayErrorModal, displaySuccessModal } from '../../../helpers/swal';
import camMap from '../mapLogic';
import {
  ANGLE_STEP,
  PLACEHOLDER,
  clampPos,
  loadRegions,
  normalizeCam,
  saveLayouts,
  snapAngle,
  toBlocks,
} from './logic';
import style from './style.module.scss';

const poly = (pts = []) => pts.map(({ x, y }) => `${x},${y}`).join(' ');

const Canvas = ({ source, onChange }) => {
  const [regions, setRegions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const commit = (fn) => {
    setRegions((prev) => {
      const next = fn(prev);
      onChangeRef.current?.(next);
      return next;
    });
  };

  const patch = (id, next) => commit((prev) => prev.map((r) => (
    r.id !== id ? r : clampPos(r, canvasRef.current, next)
  )));

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const next = toBlocks(source, canvasRef.current);
      setRegions(next);
      setSelectedId(next[0]?.id || null);
      onChangeRef.current?.(next);
    });
    return () => cancelAnimationFrame(id);
  }, [source]);

  useEffect(() => {
    const onKey = (e) => {
      if (!selectedId || (e.key !== '[' && e.key !== ']')) return;
      e.preventDefault();
      const delta = e.key === ']' ? ANGLE_STEP : -ANGLE_STEP;
      commit((prev) => prev.map((r) => (
        r.id !== selectedId
          ? r
          : clampPos(r, canvasRef.current, { angle: snapAngle(r.angle + delta) })
      )));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  const onPtrDown = (e, region, type) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(region.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = type === 'rotate'
      ? {
        type,
        id: region.id,
        cx: rect.left + rect.width / 2 + region.x,
        cy: rect.top + rect.height / 2 + region.y,
      }
      : {
        type: 'move',
        id: region.id,
        x0: e.clientX,
        y0: e.clientY,
        ox: region.x,
        oy: region.y,
      };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPtrMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.type === 'move') {
      patch(d.id, { x: d.ox + e.clientX - d.x0, y: d.oy + e.clientY - d.y0 });
      return;
    }
    patch(d.id, {
      angle: snapAngle((Math.atan2(e.clientY - d.cy, e.clientX - d.cx) * 180) / Math.PI + 90),
    });
  };

  const onPtrUp = () => { dragRef.current = null; };

  return (
    <div
      ref={canvasRef}
      className={style.canvas}
      onPointerDown={() => setSelectedId(null)}
    >
      <div className={style.world}>
        {regions.map((r) => {
          const w = r.width || PLACEHOLDER;
          const h = r.height || PLACEHOLDER;
          const selected = r.id === selectedId;
          return (
            <div
              key={r.id}
              className={[style.region, selected && style.regionOn].filter(Boolean).join(' ')}
              style={{
                left: r.x,
                top: r.y,
                width: w,
                height: h,
                marginLeft: -w / 2,
                marginTop: -h / 2,
                transform: `rotate(${r.angle}deg)`,
                borderColor: r.color,
                background: `${r.color}33`,
                zIndex: selected ? 10 : 1,
              }}
              onPointerDown={(e) => onPtrDown(e, r, 'move')}
              onPointerMove={onPtrMove}
              onPointerUp={onPtrUp}
              onPointerCancel={onPtrUp}
              onWheel={(e) => {
                if (!e.shiftKey) return;
                e.preventDefault();
                e.stopPropagation();
                setSelectedId(r.id);
                patch(r.id, {
                  angle: snapAngle(r.angle + (e.deltaY > 0 ? ANGLE_STEP : -ANGLE_STEP)),
                });
              }}
            >
              {r.masks?.length > 0 && (
                <svg className={style.mask} viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden>
                  {r.masks.map((m) => (
                    <polygon
                      key={m.id}
                      points={poly(m.points)}
                      fill={`${r.color}99`}
                      stroke={r.color}
                    />
                  ))}
                </svg>
              )}
              <span className={style.label}>{r.label}</span>
              {selected && (
                <button
                  type="button"
                  className={style.rotate}
                  aria-label="Rotate"
                  onPointerDown={(e) => onPtrDown(e, r, 'rotate')}
                  onPointerMove={onPtrMove}
                  onPointerUp={onPtrUp}
                  onPointerCancel={onPtrUp}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ArrangeModal = ({ show, onHide, onSaved }) => {
  const connectedCamera = useSelector(({ mapping }) => mapping.connectedCamera);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState([]);
  const [error, setError] = useState('');
  const regionsRef = useRef([]);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setLoading(true);
    setSource([]);
    setError('');
    regionsRef.current = [];

    loadRegions()
      .then((regions) => {
        if (cancelled) return;
        setSource(regions);
        if (!regions.length) {
          setError('No scan regions in camera mapping files. Add regions on the Grid tab first.');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSource([]);
          setError(err?.message || 'Failed to load cameras / mapping files.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [show]);

  const handleSave = async () => {
    if (!can(CONFIG)) {
      displayErrorModal({ text: 'You do not have permission to save mapping layouts.' });
      return;
    }
    const regions = regionsRef.current;
    if (!regions.length) {
      displayErrorModal({ text: 'Nothing to save.' });
      return;
    }
    setSaving(true);
    try {
      const { ok, results } = await saveLayouts(regions);
      if (!ok) {
        const detail = (results || []).filter((r) => !r.ok)
          .map((r) => `${r.camIdx}: ${r.error || 'failed'}`).join('; ');
        displayErrorModal({ text: detail || 'Failed to save layout.' });
        return;
      }
      const connected = normalizeCam(connectedCamera);
      if (connected && regions.some((r) => normalizeCam(r.camIdx) === connected)) {
        await camMap.loadMapConfig(connected);
      }
      displaySuccessModal({ text: 'Stadium layout saved to mapping files.', timer: 2000 });
      onSaved?.();
      onHide?.();
    } catch (err) {
      displayErrorModal({ text: err?.message || 'Failed to save layout.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      size="xl"
      dialogClassName={style.modalDialog}
      backdrop="static"
      centered
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Arrange regions</Modal.Title>
      </Modal.Header>
      <Modal.Body className={style.modalBody}>
        {loading ? (
          <div className={style.loading}><Spinner animation="border" /></div>
        ) : (
          <>
            {error && !source.length && <div className={style.error}>{error}</div>}
            {!error || source.length ? (
              <p className={style.hint}>Drag to move · [ ] / handle / shift-wheel rotate 45°</p>
            ) : null}
            <Canvas source={source} onChange={(next) => { regionsRef.current = next; }} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || saving || !source.length}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** Arrange button + modal. */
const Arrange = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline-primary"
        className={`small ${style.trigger}`}
        onClick={() => setOpen(true)}
      >
        Arrange
      </Button>
      <ArrangeModal show={open} onHide={() => setOpen(false)} onSaved={onSaved} />
    </>
  );
};

export default Arrange;
